import React, { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { KindTag } from '../components/shared/KindTag';
import { ServiceGlyph } from '../components/shared/ServiceGlyph';
import { WorkTile } from '../components/shared/WorkTile';
import { Lightbox } from '../components/shared/Lightbox';
import { FaqAccordion } from '../components/shared/FaqAccordion';
import { CtaBand } from '../components/shared/CtaBand';
import { Reveal } from '../components/shared/Reveal';
import { useLanguage } from '../contexts/LanguageContext';
import { balloonPalettes, serviceMeta } from '../data/site';
import { useProjects } from '../hooks/useProjects';
import { useSeo } from '../hooks/useSeo';
import { whatsappLink } from '../utils/whatsapp';
export function ServiceDetail() {
  const {
    slug
  } = useParams();
  const {
    t,
    lang
  } = useLanguage();
  const projects = useProjects();
  const [index, setIndex] = useState<number | null>(null);
  const meta = serviceMeta.find(m => m.slug === slug);
  const item = t.services.items.find(s => s.slug === slug);
  const detail = slug ? t.serviceDetails.pages[slug] : undefined;
  const labels = t.serviceDetails.labels;
  const examples = useMemo(() => {
    if (!meta) return [];
    const related = projects.filter(p => p.categories.some(c => meta.examples.includes(c)));
    return [...related.filter(p => p.image), ...related.filter(p => !p.image)].slice(0, 3);
  }, [meta, projects]);
  const viewable = examples.filter(p => p.image);
  useSeo({
    title: item ? `${item.title} | ${t.common.seo.detail.suffix}` : t.common.seo.services.title,
    description: detail ? `${detail.promise} ${detail.intro}` : t.common.seo.services.description,
    lang
  });
  if (!meta || !item || !detail) return <Navigate to="/services" replace data-id="element-516" data-mp="6syxaz6evr9s" />;
  const message = `${labels.whatsappPrefix} ${item.title}`;
  const faqItems = detail.faq.map(i => t.faq.items[i]);
  return <main data-id="element-517" data-mp="2dgg68bi97nxj">
      <section className="bg-cream" data-id="element-518" data-mp="2dg6b69icqqkz">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-12 sm:px-8 lg:grid-cols-2 lg:gap-16 lg:py-16" data-id="element-519" data-mp="2dh7pqnyktv7g">
          <div data-id="element-520" data-mp="23q3adn4zo8c3">
            <Link to="/services" className="inline-flex h-10 items-center gap-2 text-sm text-ink/60 transition-colors duration-150 ease-smooth hover:text-mauve" data-id="element-521" data-mp="23pts6lq6r5gy">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-522" data-mp="2wfakljdt2md6" />
              {labels.back}
            </Link>
            <div className="mt-6 flex items-center gap-3" data-id="element-523" data-mp="2rgqwpdfnj0i6">
              <ServiceGlyph icon={meta.icon} className="h-7 w-7 text-mauve" data-id="element-524" data-mp="2ne2lx07dnjik" />
              <KindTag kind={meta.kind} label={t.common.kinds[meta.kind]} tone="dark" data-id="element-525" data-mp="2ng70cc2wk0x3" />
            </div>
            <h1 className="mt-5 font-display text-4xl font-semibold leading-tight text-ink sm:text-5xl" data-id="element-526" data-mp="2ih3sykzg9ily">{item.title}</h1>
            <p className="mt-4 text-lg leading-relaxed text-ink/80" data-id="element-527" data-mp="16zuekcv6hgri">{detail.promise}</p>
            <p className="mt-3 text-[15px] leading-relaxed text-ink/65" data-id="element-528" data-mp="17271z5jpgjby">{detail.intro}</p>
            <div className="mt-8 flex flex-wrap items-center gap-5" data-id="element-529" data-mp="3il3aq5wq0bd4">
              <a href={whatsappLink(message)} target="_blank" rel="noreferrer" className="flex h-12 items-center rounded-full bg-mauve px-7 text-sm font-medium text-white transition-colors duration-150 ease-smooth hover:bg-mauve-deep" data-id="element-530" data-mp="3nn29t3lfgm49">
                {labels.quote}
              </a>
              <p className="text-sm text-ink/60" data-id="element-531" data-mp="a6uwk2uy3n4h">
                {labels.from} <span className="text-lg font-semibold text-mauve-deep" data-id="element-532" data-mp="a97dccoyvrfy">{item.price}</span>
              </p>
            </div>
          </div>
          <div className="overflow-hidden rounded-3xl bg-white" data-id="element-533" data-mp="j8cbhhwm9irl">
            <img src={meta.image} alt={item.title} className="aspect-[4/3] w-full object-cover" data-id="element-534" data-mp="j5q17aiccp5o" />
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20" data-id="element-535" data-mp="2l0gycez9dpvg">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12" data-id="element-536" data-mp="2l18ubqwrppm9">
          <Reveal className="lg:col-span-5" data-id="element-537" data-mp="1prc2zbnx44ql">
            <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl" data-id="element-538" data-mp="1lo73zj31ql6p">{labels.included}</h2>
            <ul className="mt-6 space-y-4" data-id="element-539" data-mp="1znovi67q8cjr">
              {detail.included.map(line => <li key={line} className="flex items-start gap-3 text-[15px] text-ink/80" data-id="element-540" data-mp="1uo2gbr8c8m8b">
                  <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-mauve" aria-hidden="true" data-id="element-541" data-mp="16y86lijbfdsa" />
                  {line}
                </li>)}
            </ul>
          </Reveal>
          <Reveal className="lg:col-span-7" data-id="element-542" data-mp="1goay1easgjoi">
            <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl" data-id="element-543" data-mp="1gq5ikri5e9s2">{detail.optionsTitle}</h2>
            <dl className="mt-6 divide-y divide-petal border-y border-petal" data-id="element-544" data-mp="1bwjgft9m1oai">
              {detail.options.map(opt => <div key={opt.name} className="grid gap-1 py-5 sm:grid-cols-[200px_1fr] sm:gap-6" data-id="element-545" data-mp="j96spfrprafm">
                  <dt className="font-medium text-mauve-deep" data-id="element-546" data-mp="2uwinuicdz8i3">{opt.name}</dt>
                  <dd className="text-[15px] text-ink/75" data-id="element-547" data-mp="2uufoh7pcg30p">{opt.detail}</dd>
                </div>)}
            </dl>

            {meta.showPalettes ? <div className="mt-10" data-id="element-548" data-mp="2lwvoe237wcro">
                <h3 className="text-xs font-medium uppercase tracking-wider2 text-ink/55" data-id="element-549" data-mp="2ltrceijigcqg">{labels.palettesTitle}</h3>
                <ul className="mt-4 flex flex-wrap gap-6" data-id="element-550" data-mp="3dn0bw9m9xfx4">
                  {balloonPalettes.map(palette => <li key={palette.id} data-id="element-551" data-mp="3immruqthla9m">
                      <div className="flex -space-x-2" aria-hidden="true" data-id="element-552" data-mp="3ill53p33yzc2">
                        {palette.colors.map(color => <span key={color} className="h-9 w-9 rounded-full border-2 border-white shadow-sm ring-1 ring-ink/10" style={{
                    backgroundColor: color
                  }} data-id="element-553" data-mp="39lety9uvubg2" />)}
                      </div>
                      <p className="mt-2 text-sm text-ink/75" data-id="element-554" data-mp="3qay5g32x8s0">{labels.palettes[palette.id]}</p>
                    </li>)}
                </ul>
              </div> : null}
          </Reveal>
        </div>
      </section>

      {examples.length > 0 ? <section className="bg-cream py-16 sm:py-20" data-id="element-555" data-mp="3m4ag6bfb1ode">
          <div className="mx-auto max-w-7xl px-5 sm:px-8" data-id="element-556" data-mp="3m3iubsbif50b">
            <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-557" data-mp="3h5h4mojkw7fv">
              <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl" data-id="element-558" data-mp="3c5upg9k6wh4f">{labels.examples}</h2>
              <Link to={`/portfolio?category=${meta.examples[0]}`} className="group inline-flex items-center gap-2 text-sm font-medium text-mauve" data-id="element-559" data-mp="376i0nj9rczz7">
                {labels.seeAll}
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-smooth group-hover:translate-x-1" data-id="element-560" data-mp="15edfqrqof2xz" />
              </Link>
            </div>
            <div className="mt-10 grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3" data-id="element-561" data-mp="2jbogcxilimat">
              {examples.map(project => <WorkTile key={project.id} project={project} viewLabel={t.home.work.viewProject} onOpen={() => setIndex(viewable.findIndex(v => v.id === project.id))} data-id="element-562" data-mp="2o9hm5pfzleak" />)}
            </div>
          </div>
          <Lightbox items={viewable} index={index} onClose={() => setIndex(null)} onChange={setIndex} data-id="element-563" data-mp="20dvnyp5lluro" />
        </section> : null}

      <section className="bg-white pt-16 sm:pt-20" data-id="element-564" data-mp="25di2d1x5xh22">
        <div className="mx-auto max-w-3xl px-5 sm:px-8" data-id="element-565" data-mp="29c7aog3cg8eu">
          <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl" data-id="element-566" data-mp="2ecbticrq5f88">{labels.faqTitle}</h2>
          <div className="mt-8" data-id="element-567" data-mp="3ksffypa5txu7">
            <FaqAccordion items={faqItems} idPrefix={`svc-${meta.slug}`} data-id="element-568" data-mp="3krdvxvb6210c" />
          </div>
        </div>
      </section>

      <CtaBand title={labels.ctaTitle} text={labels.ctaText} message={message} data-id="element-569" data-mp="3ppxjg05erkut" />
    </main>;
}